import React, { useEffect, useState, forwardRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { FormControl, InputLabel, MenuItem, Select, TextField, useTheme, useMediaQuery } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import MaterialTable, { serialNumberColumn } from '../components/MaterialTableAdapter'
import { toast } from 'react-toastify'
import {
  Building2,
  CalendarDays,
  FileDown,
  FileSpreadsheet,
  LogOut,
  MapPin,
  Trash2,
  UserRoundX,
} from 'lucide-react'
import { FilterCard, FilterToggle } from '../ui'

import Loading from '../components/Loading'
import AddBox from '@mui/icons-material/AddBox'
import ArrowDownward from '@mui/icons-material/ArrowDownward'
import Check from '@mui/icons-material/Check'
import ChevronLeft from '@mui/icons-material/ChevronLeft'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Clear from '@mui/icons-material/Clear'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import Edit from '@mui/icons-material/Edit'
import FilterList from '@mui/icons-material/FilterList'
import FirstPage from '@mui/icons-material/FirstPage'
import LastPage from '@mui/icons-material/LastPage'
import Remove from '@mui/icons-material/Remove'
import SaveAlt from '@mui/icons-material/SaveAlt'
import Search from '@mui/icons-material/Search'
import ViewColumn from '@mui/icons-material/ViewColumn'
import { exportToExcel, exportToPDF } from '../utils/exportUtils'
import { handleUploadImageError, resolveUploadUrl } from '../utils/uploadUrl'

import { forceLogoutUser, forceLogoutMultipleUsers } from '../actions/AdminAction'
import { getRegularUsers } from '../actions/userActions'
import { deletePlayer, bulkDeletePlayers } from '../actions/playerActions'
import {
  FORCE_LOGOUT_USER_RESET,
  FORCE_LOGOUT_MULTIPLE_RESET,
} from '../constants/adminConstant'
import {
  DELETE_PLAYER_RESET,
  BULK_DELETE_PLAYERS_RESET,
} from '../constants/playerConstants'
import ScreenHeader from '../components/layout/ScreenHeader'
import { hasPermission } from '../constants/rolePermissions'


const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => (
    <ChevronRight {...props} ref={ref} />
  )),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => (
    <ChevronLeft {...props} ref={ref} />
  )),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowDownward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
}

const IMAGE_NOT_FOUND_PLACEHOLDER = `data:image/svg+xml;utf8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="300" height="200"><rect width="100%" height="100%" fill="#f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="var(--cm-text-muted)" font-size="16" font-family="Arial">Image Not Found</text></svg>',
)}`;

const Players = ({ history }) => {
  const navigate = useNavigate();
  const [data, setData] = useState([])
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true)
  const [clubs, setClubs] = useState([])
  const [playerFilters, setPlayerFilters] = useState({
    venueId: '',
    clubId: '',
    fromDate: '',
    toDate: '',
  })
  const [playerSearch, setPlayerSearch] = useState('')
  const [filtersOpen, setFiltersOpen] = useState(false)

  // Force Logout States
  const [forceLogoutModal, setForceLogoutModal] = useState(false)
  const [selectedUserForLogout, setSelectedUserForLogout] = useState(null)
  const [selectedUsersForBulkLogout, setSelectedUsersForBulkLogout] = useState([])
  const [bulkForceLogoutModal, setBulkForceLogoutModal] = useState(false)

  // Delete modals
  const [deleteModal, setDeleteModal] = useState(false)
  const [selectedUserForDelete, setSelectedUserForDelete] = useState(null)
  const [bulkDeleteModal, setBulkDeleteModal] = useState(false)

  // Role permissions
  const [forceLogoutOption, setForceLogoutOption] = useState(false)
  const [deleteOption, setDeleteOption] = useState(false)
  const [exportOption, setExportOption] = useState(false)

  // KYC Image Modal States
  const [kycModalOpen, setKycModalOpen] = useState(false)
  const [selectedKycImages, setSelectedKycImages] = useState(null)

  const dispatch = useDispatch()
  const loadPlayers = (search = playerSearch, filters = playerFilters) =>
    dispatch(getRegularUsers(search, filters))

  // Check for auth
  const userLogin = useSelector((state) => state.adminLogin)
  const { userInfo } = userLogin

  useEffect(() => {
    if (!userInfo) {
      navigate('/')
    }
  }, [userInfo, navigate])

  // Check for role
  const getRoleInfo = useSelector((state) => state.getRoleInfo)
  const { getRoleData } = getRoleInfo

  useEffect(() => {
    const roleData = getRoleData || userInfo?.role

    if (roleData) {
      setForceLogoutOption(hasPermission(roleData, 'usersUpdate'))
      setDeleteOption(hasPermission(roleData, 'playersDelete'))
      setExportOption(hasPermission(roleData, 'playersExport'))
      // Check playersView permission
      if (!hasPermission(roleData, 'playersView')) {
        toast('You do not have permission to view this page', {
          type: 'error',
          hideProgressBar: true,
          autoClose: 2000,
        })
        navigate('/dashboard')
      }
    }
  }, [userInfo, getRoleData, navigate])

  // Fetch players on mount
  const hasFetched = React.useRef(false)

  useEffect(() => {
    if (userInfo && !hasFetched.current) {
      loadPlayers('', playerFilters)
      hasFetched.current = true
    }
  }, [dispatch, userInfo?.id])

  useEffect(() => {
    if (!userInfo?.token) return
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api'
    axios
      .get(`${apiUrl}/users/player-filter-options`, {
        headers: { Authorization: `Bearer ${userInfo.token}` },
      })
      .then(({ data }) => setClubs(data.clubs || []))
      .catch(() => setClubs([]))
  }, [userInfo?.token])

  const venues = React.useMemo(() => {
    const unique = new Map()
    clubs.forEach((club) => {
      if (club.location?._id) unique.set(String(club.location._id), club.location)
    })
    return [...unique.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [clubs])

  const availableClubs = React.useMemo(
    () => playerFilters.venueId
      ? clubs.filter((club) => String(club.location?._id) === String(playerFilters.venueId))
      : clubs,
    [clubs, playerFilters.venueId],
  )

  const changeVenue = (event) => {
    const nextFilters = { ...playerFilters, venueId: event.target.value, clubId: '' }
    setPlayerFilters(nextFilters)
    setSelectedUsersForBulkLogout([])
    loadPlayers(playerSearch, nextFilters)
  }

  const changeClub = (event) => {
    const nextFilters = { ...playerFilters, clubId: event.target.value }
    setPlayerFilters(nextFilters)
    setSelectedUsersForBulkLogout([])
    loadPlayers(playerSearch, nextFilters)
  }

  const changeDate = (field) => (event) => {
    const nextFilters = { ...playerFilters, [field]: event.target.value }
    setPlayerFilters(nextFilters)
    setSelectedUsersForBulkLogout([])
    loadPlayers(playerSearch, nextFilters)
  }

  // Get Regular Users (Players)
  const getRegularUsersInfo = useSelector((state) => state.getUsersInfo)
  const { loadingGetRegularUsers, errorGetRegularUsers, getUsersData: getRegularUsersData } = getRegularUsersInfo

  useEffect(() => {
    if (getRegularUsersData) {
      setLoading(false)
      if (Array.isArray(getRegularUsersData)) {
        setData(getRegularUsersData)
        setFilteredData(getRegularUsersData)
      }
    } else if (errorGetRegularUsers) {
      setLoading(false)
      toast(errorGetRegularUsers, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [getRegularUsersData, errorGetRegularUsers])

  // Force Logout Single User
  const openForceLogoutModal = (user) => {
    setSelectedUserForLogout(user)
    setForceLogoutModal(true)
  }

  const closeForceLogoutModal = () => {
    setSelectedUserForLogout(null)
    setForceLogoutModal(false)
  }

  const handleForceLogout = () => {
    if (selectedUserForLogout) {
      dispatch(forceLogoutUser(selectedUserForLogout._id))
    }
  }

  // Force Logout Multiple Users
  const openBulkForceLogoutModal = () => {
    if (selectedUsersForBulkLogout.length === 0) {
      toast('Please select players to force logout', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }
    setBulkForceLogoutModal(true)
  }

  const closeBulkForceLogoutModal = () => {
    setBulkForceLogoutModal(false)
  }

  const handleBulkForceLogout = () => {
    const userIds = selectedUsersForBulkLogout.map(user => user._id)
    dispatch(forceLogoutMultipleUsers(userIds))
  }

  // Delete Single Player
  const openDeleteModal = (user) => {
    setSelectedUserForDelete(user)
    setDeleteModal(true)
  }

  const closeDeleteModal = () => {
    setSelectedUserForDelete(null)
    setDeleteModal(false)
  }

  const handleDeletePlayer = () => {
    if (selectedUserForDelete) {
      dispatch(deletePlayer(selectedUserForDelete._id)).catch(() => { })
    }
  }

  // Delete Multiple Players
  const openBulkDeleteModal = () => {
    if (selectedUsersForBulkLogout.length === 0) {
      toast('Please select players to delete', {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
      return
    }
    setBulkDeleteModal(true)
  }

  const closeBulkDeleteModal = () => {
    setBulkDeleteModal(false)
  }

  const handleBulkDeletePlayers = () => {
    const playerIds = selectedUsersForBulkLogout.map(user => user._id)
    dispatch(bulkDeletePlayers(playerIds)).catch(() => { })
  }

  // Export Handlers
  const prepareExportData = (dataToExport) => {
    return dataToExport.map(user => {
      let displayName = user.name;
      const verification = user.verification || {};

      if (!displayName && verification.manualKYC) {
        displayName = verification.manualKYC.pan?.extractedData?.name ||
                      verification.manualKYC.aadhaar?.front?.extractedData?.name;
      }
      if (!displayName) {
        displayName = verification.pan?.name || verification.aadhaar?.name;
      }
      displayName = displayName || user.identifier || 'N/A';

      const currentUserId = JSON.parse(localStorage.getItem('adminInfo'))?.id;
      const isCurrentUser = user._id === currentUserId;
      let isOnline = false;
      if (user.lastLogin) {
        const lastLoginTime = new Date(user.lastLogin);
        const now = new Date();
        const diffMinutes = (now - lastLoginTime) / (1000 * 60);
        isOnline = diffMinutes < 30;
      }
      const online = isCurrentUser || isOnline;

      const isAadhaarVerified = verification.aadhaar?.verified || false;
      const isPanVerified = verification.pan?.verified || false;
      const isBankVerified = verification.bank?.verified || false;
      const isManualVerified = verification.verificationMethod === 'manual' ||
                               verification.manualKYC?.status === 'approved';
      const isFullyVerified = (isAadhaarVerified && isPanVerified && isBankVerified) || isManualVerified;

      return {
        Name: displayName,
        Identifier: user.identifier || 'N/A',
        Email: user.email || 'N/A',
        Phone: user.phoneNumber || 'N/A',
        KYC: isFullyVerified ? 'Verified' : 'Pending',
        'Club Origin': user.clubOrigin?.clubName || 'N/A',
        'Join Date': user.joinedOn ? new Date(user.joinedOn).toLocaleDateString() : 'N/A',
        Status: online ? 'Online' : 'Offline',
        Balance: user.availableBalance || 0,
        Notes: user.remarksPreview || ''
      };
    });
  };

  const handleExportExcel = () => {
    const exportData = prepareExportData(filteredData);
    const columns = [
      { title: 'Name', field: 'Name' },
      { title: 'Identifier', field: 'Identifier' },
      { title: 'Email', field: 'Email' },
      { title: 'Phone', field: 'Phone' },
      { title: 'KYC', field: 'KYC' },
      { title: 'Club Origin', field: 'Club Origin' },
      { title: 'Join Date', field: 'Join Date' },
      { title: 'Status', field: 'Status' },
      { title: 'Balance', field: 'Balance' },
      { title: 'Notes', field: 'Notes' }
    ];
    exportToExcel(exportData, columns, 'Players');
  };

  const handleExportPDF = () => {
    const exportData = prepareExportData(filteredData);
    const columns = [
      { title: 'Name', field: 'Name' },
      { title: 'Identifier', field: 'Identifier' },
      { title: 'Email', field: 'Email' },
      { title: 'Phone', field: 'Phone' },
      { title: 'KYC', field: 'KYC' },
      { title: 'Club Origin', field: 'Club Origin' },
      { title: 'Join Date', field: 'Join Date' },
      { title: 'Status', field: 'Status' },
      { title: 'Balance', field: 'Balance' },
      { title: 'Notes', field: 'Notes' }
    ];
    exportToPDF(exportData, columns, 'Players', 'Players List');
  };

  // Close KYC Modal
  const closeKycModal = () => {
    setKycModalOpen(false)
    setSelectedKycImages(null)
  }

  // Table columns
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const getHeadCells = () => {
    const baseColumns = [
      serialNumberColumn,
      {
        field: 'name',
        title: 'Name',
        render: (rowData) => {
          let displayName = rowData.name;

          if (!displayName && rowData.verification?.manualKYC) {
            displayName = rowData.verification.manualKYC.pan?.extractedData?.name ||
                         rowData.verification.manualKYC.aadhaar?.front?.extractedData?.name;
          }

          if (!displayName && rowData.verification) {
            displayName = rowData.verification.pan?.name || rowData.verification.aadhaar?.name;
          }

          return (
            <Link
              className='inline-flex font-semibold text-cm-text hover:text-cm-gold-text underline-offset-4 hover:underline truncate max-w-[160px] sm:max-w-none'
              to={`/players/${rowData._id}`}
            >
              {displayName || rowData.identifier || 'N/A'}
            </Link>
          );
        },
        cellStyle: { padding: isMobile ? '8px 4px' : '16px' }
      },
      // Hide Email on Mobile
      ...(!isMobile ? [{
        field: 'email',
        title: 'Email',
        render: (rowData) => rowData.email || 'N/A',
      }] : []),
      // Hide Identifier on Tablet/Mobile
      ...(!isTablet ? [{
        field: 'identifier',
        title: 'Identifier',
      }] : [])
    ]

    // Phone Number column - Show on Tablet, Hide on Mobile
    if (!isMobile) {
      baseColumns.splice(3, 0, {
        field: 'phoneNumber',
        title: 'Phone Number',
        render: (rowData) => rowData.phoneNumber || 'N/A',
      })
    }

    // KYC Status
    baseColumns.push({
      field: 'verification',
      title: 'KYC',
      render: (rowData) => {
        const verification = rowData.verification || {};
        const isAadhaarVerified = verification.aadhaar?.verified || false;
        const isPanVerified = verification.pan?.verified || false;
        const isBankVerified = verification.bank?.verified || false;
        const isManualVerified = verification.verificationMethod === 'manual' ||
                                 verification.manualKYC?.status === 'approved' ||
                                 (verification.pan?.verificationMethod === 'manual' && verification.aadhaar?.verificationMethod === 'manual');
        const isFullyVerified = (isAadhaarVerified && isPanVerified && isBankVerified) || isManualVerified;

        if (isFullyVerified) {
          const hasManualImages = verification.manualKYC && (
            verification.manualKYC.pan?.imageUrl ||
            verification.manualKYC.aadhaar?.front?.imageUrl ||
            verification.manualKYC.aadhaar?.back?.imageUrl
          );

          return (
            <div className="flex flex-col gap-1 items-center">
              <span className="px-2 py-1 rounded text-[10px] sm:text-xs font-bold bg-green-100 text-green-800 text-center whitespace-nowrap">
                {isMobile ? '✓' : (isManualVerified ? '✓ Verified' : '✓ Fully Verified')}
              </span>
              {isManualVerified && hasManualImages && (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedKycImages({
                      name: rowData.name || rowData.identifier || rowData.phoneNumber || 'N/A',
                      ...verification.manualKYC
                    });
                    setKycModalOpen(true);
                  }}
                  className="text-[10px] sm:text-xs text-blue-600 hover:text-blue-800 underline font-bold mt-1 cursor-pointer z-10"
                  style={{ position: 'relative' }}
                >
                  {isMobile ? 'Pics' : 'View Pictures'}
                </button>
              )}
            </div>
          );
        }

        const pendingDocs = [];
        if (!isAadhaarVerified) pendingDocs.push('Aadhaar');
        if (!isPanVerified) pendingDocs.push('PAN');
        if (!isBankVerified) pendingDocs.push('Bank');

        return (
          <div className="flex flex-col gap-1">
            <span className="px-2 py-1 rounded text-[10px] sm:text-xs font-bold bg-yellow-100 text-yellow-800 text-center whitespace-nowrap">
              {isMobile ? 'Pending' : 'Pending KYC'}
            </span>
            {!isMobile && (
              <span className="text-xs text-gray-600 text-center font-medium">
                {pendingDocs.join(', ')}
              </span>
            )}
          </div>
        );
      },
      cellStyle: { padding: isMobile ? '8px 4px' : '16px' }
    });

    // Club origin: club of the player's first check-in
    if (!isMobile) {
      baseColumns.push({
        field: 'clubOrigin.clubName',
        title: 'Club Origin',
        render: (rowData) => rowData.clubOrigin ? (
          <div className="flex flex-col" title={`First check-in: ${new Date(rowData.clubOrigin.checkedInAt).toLocaleString()}`}>
            <span className="text-sm font-semibold text-cm-text whitespace-nowrap">
              {rowData.clubOrigin.clubName || 'Unknown club'}
            </span>
            {rowData.clubOrigin.checkedInAt && (
              <span className="text-xs text-cm-muted whitespace-nowrap">
                {new Date(rowData.clubOrigin.checkedInAt).toLocaleDateString()}
              </span>
            )}
          </div>
        ) : (
          <span className="text-gray-400 text-xs">—</span>
        ),
      });
    }

    // Balance
    if (!isMobile) {
      baseColumns.push({
          field: 'availableBalance',
          title: 'Bal',
          render: (rowData) => (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium whitespace-nowrap">
              ₹{(rowData.availableBalance || 0).toLocaleString()}
            </span>
          ),
      });
    }

    // Remarks Preview - Show on desktop only
    if (!isTablet) {
      baseColumns.push({
        field: 'remarksPreview',
        title: 'Notes',
        render: (rowData) => {
          if (!rowData.hasRemarks) {
            return <span className="text-gray-400 text-xs">—</span>;
          }

          // Category color mapping
          const getCategoryColor = (category) => {
            switch (category) {
              case 'fraud_suspected': return 'bg-red-100 text-red-700 border-red-300';
              case 'account_issue': return 'bg-orange-100 text-orange-700 border-orange-300';
              case 'verification_pending': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
              case 'vip_player': return 'bg-purple-100 text-purple-700 border-purple-300';
              case 'compliance': return 'bg-blue-100 text-blue-700 border-blue-300';
              default: return 'bg-gray-100 text-gray-700 border-gray-300';
            }
          };

          return (
            <div className="max-w-[120px]" title={rowData.remarksPreview}>
              <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium border ${getCategoryColor(rowData.remarksCategory)} truncate max-w-full`}>
                {rowData.remarksPreview || 'Note'}
              </span>
            </div>
          );
        },
        cellStyle: { padding: '8px', maxWidth: '150px' }
      });
    }

    // Joined On - Hide on mobile
    if (!isTablet) {
      baseColumns.push({
        field: 'joinedOn',
        title: 'Joined',
        render: (rowData) => {
          if (rowData.joinedOn) {
            try {
              return new Date(rowData.joinedOn).toLocaleDateString()
            } catch (error) {
              return rowData.joinedOn
            }
          }
          return 'N/A'
        },
      });
    }

    // Status Column
    baseColumns.push({
      field: 'calling',
      title: 'Status',
      render: (rowData) => {
        const currentUserId = JSON.parse(localStorage.getItem('adminInfo'))?.id;
        const isCurrentUser = rowData._id === currentUserId;

        let isOnline = false;
        if (rowData.lastLogin) {
          const lastLoginTime = new Date(rowData.lastLogin);
          const now = new Date();
          const diffMinutes = (now - lastLoginTime) / (1000 * 60);
          isOnline = diffMinutes < 30;
        }

        const online = isCurrentUser || isOnline;

        return (
          <span className={`px-2 py-1 rounded text-[10px] sm:text-xs font-medium ${online
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
            }`}>
            {online ? 'Online' : 'Offline'}
          </span>
        );
      },
      cellStyle: { padding: isMobile ? '8px 4px' : '16px' }
    });

    // Actions Column
    baseColumns.push({
      title: 'Action',
      sorting: false,
      headerClassName: 'action-column-header',
      bodyClassName: 'action-column-cell',
      headerStyle: { textAlign: 'center', width: 140, minWidth: 140 },
      render: (rowData) => {
        if (!rowData || !rowData._id) return null;

        return (
          <div className='flex w-full items-center justify-center gap-2'>
            {forceLogoutOption && userInfo?.id !== rowData._id && (
              <button
                onClick={() => openForceLogoutModal(rowData)}
                className='inline-flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200 transition-all hover:-translate-y-0.5 hover:bg-amber-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-amber-400'
                title='Force Logout'
                aria-label='Force Logout'
              >
                <UserRoundX size={16} strokeWidth={2} />
              </button>
            )}
            {deleteOption && (
              <button
                onClick={() => openDeleteModal(rowData)}
                className='inline-flex h-9 w-9 items-center justify-center rounded-xl bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200 transition-all hover:-translate-y-0.5 hover:bg-rose-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-rose-400'
                title='Delete Player'
                aria-label='Delete Player'
              >
                <Trash2 size={16} strokeWidth={2} />
              </button>
            )}
          </div>
        );
      },
      cellStyle: {
        padding: isMobile ? '8px 4px' : '16px',
        textAlign: 'center',
        width: 140,
        minWidth: 140,
      }
    });

    return baseColumns
  }

  // Force Logout Handlers
  const forceLogoutUserInfo = useSelector((state) => state.forceLogoutUserInfo)
  const { loadingForceLogoutUser, errorForceLogoutUser, forceLogoutUserData } = forceLogoutUserInfo

  useEffect(() => {
    dispatch({ type: FORCE_LOGOUT_USER_RESET })
    if (forceLogoutUserData) {
      toast(forceLogoutUserData.msg, {
        type: 'success',
        hideProgressBar: true,
        autoClose: 3000,
      })
      closeForceLogoutModal()
      setTimeout(() => {
        loadPlayers()
      }, 1000)
    } else if (errorForceLogoutUser) {
      toast(errorForceLogoutUser, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [forceLogoutUserData, errorForceLogoutUser, dispatch])

  const forceLogoutMultipleInfo = useSelector((state) => state.forceLogoutMultipleInfo)
  const { loadingForceLogoutMultiple, errorForceLogoutMultiple, forceLogoutMultipleData } = forceLogoutMultipleInfo

  useEffect(() => {
    dispatch({ type: FORCE_LOGOUT_MULTIPLE_RESET })
    if (forceLogoutMultipleData) {
      toast(forceLogoutMultipleData.msg, {
        type: 'success',
        hideProgressBar: true,
        autoClose: 3000,
      })
      setSelectedUsersForBulkLogout([])
      closeBulkForceLogoutModal()
      setTimeout(() => {
        loadPlayers()
      }, 1000)
    } else if (errorForceLogoutMultiple) {
      toast(errorForceLogoutMultiple, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 2000,
      })
    }
  }, [forceLogoutMultipleData, errorForceLogoutMultiple, dispatch])

  // Delete Handlers
  const deletePlayerInfo = useSelector((state) => state.deletePlayer)
  const {
    loading: loadingDeletePlayer,
    error: errorDeletePlayer,
    success: successDeletePlayer,
  } = deletePlayerInfo

  useEffect(() => {
    if (successDeletePlayer) {
      dispatch({ type: DELETE_PLAYER_RESET })
      toast('Player deleted successfully', {
        type: 'success',
        hideProgressBar: true,
        autoClose: 3000,
      })
      closeDeleteModal()
      loadPlayers()
    } else if (errorDeletePlayer) {
      dispatch({ type: DELETE_PLAYER_RESET })
      toast(errorDeletePlayer, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 3000,
      })
    }
  }, [successDeletePlayer, errorDeletePlayer, dispatch])

  const bulkDeletePlayersInfo = useSelector((state) => state.bulkDeletePlayers)
  const {
    loading: loadingBulkDeletePlayers,
    error: errorBulkDeletePlayers,
    success: successBulkDeletePlayers,
    deletedCount,
    skipped,
  } = bulkDeletePlayersInfo

  useEffect(() => {
    if (successBulkDeletePlayers) {
      dispatch({ type: BULK_DELETE_PLAYERS_RESET })
      toast(`${deletedCount} player(s) deleted successfully`, {
        type: 'success',
        hideProgressBar: true,
        autoClose: 3000,
      })
      if (skipped && skipped.length > 0) {
        toast(`${skipped.length} skipped: ${skipped[0].reason}`, {
          type: 'warning',
          hideProgressBar: true,
          autoClose: 4000,
        })
      }
      setSelectedUsersForBulkLogout([])
      closeBulkDeleteModal()
      loadPlayers()
    } else if (errorBulkDeletePlayers) {
      dispatch({ type: BULK_DELETE_PLAYERS_RESET })
      toast(errorBulkDeletePlayers, {
        type: 'error',
        hideProgressBar: true,
        autoClose: 3000,
      })
    }
  }, [successBulkDeletePlayers, errorBulkDeletePlayers, deletedCount, skipped, dispatch])

  const filterVenue = venues.find((venue) => venue._id === playerFilters.venueId)
  const filterClub = clubs.find((club) => club._id === playerFilters.clubId)
  const filterChips = [
    playerFilters.venueId && { icon: MapPin, label: filterVenue?.name || 'Venue' },
    playerFilters.clubId && { icon: Building2, label: filterClub?.name || 'Club' },
    (playerFilters.fromDate || playerFilters.toDate) && {
      icon: CalendarDays,
      label: `${playerFilters.fromDate || '…'} → ${playerFilters.toDate || '…'}`,
    },
  ].filter(Boolean)

  if (loading) {
    return <Loading />
  }

  return (
    <div className='w-full h-full'>
      <ScreenHeader
        title="Players"
        description={`${data.length.toLocaleString('en-IN')} players match these filters. Open a player to see their KYC, wallet and sessions.`}
        actions={
          <FilterToggle
            open={filtersOpen}
            onToggle={() => setFiltersOpen((o) => !o)}
            count={filterChips.length}
            controls='player-filters'
          />
        }
      />

      <FilterCard
        id='player-filters'
        className='mb-4'
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        chips={filterChips}
        emptyLabel='All venues, clubs and dates'
      >
        <div className='flex flex-wrap items-center gap-3'>
              <FormControl size='small' sx={{ minWidth: 210 }}>
                <InputLabel id='players-venue-filter'>Venue</InputLabel>
                <Select
                  labelId='players-venue-filter'
                  label='Venue'
                  value={playerFilters.venueId}
                  onChange={changeVenue}
                >
                  <MenuItem value=''>All venues</MenuItem>
                  {venues.map((venue) => (
                    <MenuItem key={venue._id} value={venue._id}>{venue.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size='small' sx={{ minWidth: 210 }}>
                <InputLabel id='players-club-filter'>Club</InputLabel>
                <Select
                  labelId='players-club-filter'
                  label='Club'
                  value={playerFilters.clubId}
                  onChange={changeClub}
                >
                  <MenuItem value=''>All clubs</MenuItem>
                  {availableClubs.map((club) => (
                    <MenuItem key={club._id} value={club._id}>
                      {club.name}{!playerFilters.venueId && club.location?.name ? ` · ${club.location.name}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label='From'
                type='date'
                size='small'
                value={playerFilters.fromDate}
                onChange={changeDate('fromDate')}
                inputProps={{ max: playerFilters.toDate || undefined }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
              <TextField
                label='To'
                type='date'
                size='small'
                value={playerFilters.toDate}
                onChange={changeDate('toDate')}
                inputProps={{ min: playerFilters.fromDate || undefined }}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 160 }}
              />
        </div>
      </FilterCard>

      <MaterialTable
        toolbarActions={
          <>
            {forceLogoutOption && (
              <button type='button' onClick={openBulkForceLogoutModal} title='Force Logout Selected' aria-label='Force Logout Selected' className='inline-flex h-10 w-10 items-center justify-center rounded-md bg-orange-100 text-orange-600 hover:bg-orange-200 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-orange-400'>
                <LogOut size={18} strokeWidth={2.2} />
              </button>
            )}
            {deleteOption && (
              <button type='button' onClick={openBulkDeleteModal} title='Delete Selected' aria-label='Delete Selected' className='inline-flex h-10 w-10 items-center justify-center rounded-md bg-red-100 text-red-600 hover:bg-red-200 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-red-400'>
                <Trash2 size={18} strokeWidth={2.2} />
              </button>
            )}
            {exportOption && (
              <>
                <button type='button' onClick={handleExportExcel} title='Export to Excel' aria-label='Export to Excel' className='inline-flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-inset ring-teal-200 transition-all hover:bg-teal-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-cm-gold'>
                  <FileSpreadsheet size={18} strokeWidth={2} />
                </button>
                <button type='button' onClick={handleExportPDF} title='Export to PDF' aria-label='Export to PDF' className='inline-flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200 transition-all hover:bg-violet-100 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-400'>
                  <FileDown size={18} strokeWidth={2} />
                </button>
              </>
            )}
          </>
        }
        icons={tableIcons}
        columns={getHeadCells()}
        data={data}
        options={{
          exportButton: false,
          search: true,
          exportAllData: false,
          rowStyle: {
            height: '5px',
            fontSize: 13,
          },
          paging: true,
          pageSize: 10,
          emptyRowsWhenPaging: false,
          pageSizeOptions: [10, 20, 50],
          headerStyle: {
            position: 'sticky',
            top: '0',
          },
          selection: forceLogoutOption || deleteOption,
          selectionProps: rowData => ({
            disabled: userInfo?.id === rowData._id,
          }),
          onValueChange: (value) => setFilteredData(value),
        }}
        onSearchChange={(searchText) => {
          setPlayerSearch(searchText)
          loadPlayers(searchText)
        }}
        onSelectionChange={(rows) => setSelectedUsersForBulkLogout(rows)}
      />

      {/* Force Logout Modal */}
      {forceLogoutModal && selectedUserForLogout && (
        <>
          <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
            <div className='relative w-auto my-6 mx-auto max-w-3xl'>
              <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                <div className='flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                  <h3 className='text-lg font-semibold mt-0 mb-0'>Force Logout Player</h3>
                </div>
                <div className='relative p-6 flex-auto'>
                  <p className='text-gray-500 text-sm leading-relaxed'>
                    Are you sure you want to force logout <strong>{selectedUserForLogout.name || selectedUserForLogout.identifier}</strong>?
                  </p>
                  <p className='text-gray-400 text-xs mt-2'>
                    This will immediately invalidate all their active sessions and they will need to login again.
                  </p>
                </div>
                <div className='flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b'>
                  <button
                    className='text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                    type='button'
                    onClick={closeForceLogoutModal}
                  >
                    Cancel
                  </button>
                  <button
                    className='bg-red-500 text-white active:bg-red-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300'
                    type='button'
                    onClick={handleForceLogout}
                    disabled={loadingForceLogoutUser}
                  >
                    Force Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
        </>
      )}

      {/* Bulk Force Logout Modal */}
      {bulkForceLogoutModal && (
        <>
          <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
            <div className='relative w-auto my-6 mx-auto max-w-md'>
              <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                <div className='flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                  <h3 className='text-lg font-semibold mt-0 mb-0'>Force Logout Multiple Players</h3>
                </div>
                <div className='relative p-6 flex-auto'>
                  <p className='text-gray-500 text-sm leading-relaxed mb-3'>
                    Are you sure you want to force logout <strong>{selectedUsersForBulkLogout.length}</strong> selected players?
                  </p>
                  <div className='max-h-32 overflow-y-auto'>
                    {selectedUsersForBulkLogout.map((user, index) => (
                      <p key={index} className='text-xs text-gray-600'>• {user.name || user.identifier} ({user.email || user.phoneNumber})</p>
                    ))}
                  </div>
                  <p className='text-gray-400 text-xs mt-2'>
                    This will immediately invalidate all their active sessions and they will need to login again.
                  </p>
                </div>
                <div className='flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b'>
                  <button
                    className='text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                    type='button'
                    onClick={closeBulkForceLogoutModal}
                  >
                    Cancel
                  </button>
                  <button
                    className='bg-red-500 text-white active:bg-red-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300'
                    type='button'
                    onClick={handleBulkForceLogout}
                    disabled={loadingForceLogoutMultiple}
                  >
                    Force Logout All
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
        </>
      )}

      {/* Delete Player Modal */}
      {deleteModal && selectedUserForDelete && (
        <>
          <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
            <div className='relative w-auto my-6 mx-auto max-w-md'>
              <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                <div className='flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                  <h3 className='text-lg font-semibold mt-0 mb-0 text-red-700'>Delete Player</h3>
                </div>
                <div className='relative p-6 flex-auto'>
                  <p className='text-gray-500 text-sm leading-relaxed'>
                    Are you sure you want to permanently delete <strong>{selectedUserForDelete.name || selectedUserForDelete.identifier}</strong>?
                  </p>
                  <p className='text-red-600 text-xs mt-3 font-semibold'>
                    This cannot be undone. The player account will be permanently removed.
                  </p>
                </div>
                <div className='flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b'>
                  <button
                    className='text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                    type='button'
                    onClick={closeDeleteModal}
                  >
                    Cancel
                  </button>
                  <button
                    className='bg-red-700 text-white active:bg-red-800 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300'
                    type='button'
                    onClick={handleDeletePlayer}
                    disabled={loadingDeletePlayer}
                  >
                    {loadingDeletePlayer ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
        </>
      )}

      {/* Bulk Delete Players Modal */}
      {bulkDeleteModal && (
        <>
          <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
            <div className='relative w-auto my-6 mx-auto max-w-md'>
              <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none'>
                <div className='flex items-center justify-between py-1 px-3 border-b border-solid border-blueGray-200 rounded-t'>
                  <h3 className='text-lg font-semibold mt-0 mb-0 text-red-700'>Delete Multiple Players</h3>
                </div>
                <div className='relative p-6 flex-auto'>
                  <p className='text-gray-500 text-sm leading-relaxed mb-3'>
                    Are you sure you want to permanently delete <strong>{selectedUsersForBulkLogout.length}</strong> selected players?
                  </p>
                  <div className='max-h-32 overflow-y-auto'>
                    {selectedUsersForBulkLogout.map((user, index) => (
                      <p key={index} className='text-xs text-gray-600'>• {user.name || user.identifier} ({user.email || user.phoneNumber})</p>
                    ))}
                  </div>
                  <p className='text-red-600 text-xs mt-3 font-semibold'>
                    This cannot be undone. These player accounts will be permanently removed.
                  </p>
                </div>
                <div className='flex items-center justify-end py-2 px-3 border-t border-solid border-blueGray-200 rounded-b'>
                  <button
                    className='text-gray-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150'
                    type='button'
                    onClick={closeBulkDeleteModal}
                  >
                    Cancel
                  </button>
                  <button
                    className='bg-red-700 text-white active:bg-red-800 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 disabled:bg-gray-300'
                    type='button'
                    onClick={handleBulkDeletePlayers}
                    disabled={loadingBulkDeletePlayers}
                  >
                    {loadingBulkDeletePlayers ? 'Deleting...' : 'Delete All'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='opacity-25 fixed inset-0 z-40 bg-black'></div>
        </>
      )}

      {/* KYC Images Modal */}
      {kycModalOpen && selectedKycImages && (
        <>
          <div className='justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none'>
            <div className='relative w-full my-6 mx-auto max-w-4xl'>
              <div className='border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none max-h-[90vh]'>
                {/* Header */}
                <div className='flex items-center justify-between py-3 px-4 border-b border-solid border-gray-200 rounded-t bg-gray-50'>
                  <h3 className='text-lg font-semibold text-gray-800'>
                    KYC Documents - {selectedKycImages.name}
                  </h3>
                  <button
                    className='text-gray-400 hover:text-gray-600'
                    onClick={closeKycModal}
                  >
                    <Clear />
                  </button>
                </div>

                {/* Body */}
                <div className='relative p-6 flex-auto overflow-y-auto'>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* PAN Card */}
                    {selectedKycImages.pan?.imageUrl && (
                      <div className="flex flex-col border rounded-lg p-2 shadow-sm">
                        <span className="font-bold text-gray-700 mb-2 border-b pb-1">PAN Card</span>
                        <div className="flex-1 flex items-center justify-center bg-gray-100 rounded">
                          <img
                            src={resolveUploadUrl(selectedKycImages.pan.imageUrl)}
                            alt="PAN Card"
                            className="w-full object-contain hover:scale-105 transition-transform duration-200 cursor-zoom-in"
                            onClick={() => {
                              const imageUrl = resolveUploadUrl(selectedKycImages.pan.imageUrl)
                              if (imageUrl) window.open(imageUrl, '_blank')
                            }}
                            onError={(e) => {
                                handleUploadImageError(e, selectedKycImages.pan.imageUrl, IMAGE_NOT_FOUND_PLACEHOLDER)
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Aadhaar Front */}
                    {selectedKycImages.aadhaar?.front?.imageUrl && (
                      <div className="flex flex-col border rounded-lg p-2 shadow-sm">
                        <span className="font-bold text-gray-700 mb-2 border-b pb-1">Aadhaar Front</span>
                        <div className="flex-1 flex items-center justify-center bg-gray-100 rounded">
                          <img
                            src={resolveUploadUrl(selectedKycImages.aadhaar.front.imageUrl)}
                            alt="Aadhaar Front"
                            className="w-full object-contain hover:scale-105 transition-transform duration-200 cursor-zoom-in"
                            onClick={() => {
                              const imageUrl = resolveUploadUrl(selectedKycImages.aadhaar.front.imageUrl)
                              if (imageUrl) window.open(imageUrl, '_blank')
                            }}
                             onError={(e) => {
                                handleUploadImageError(e, selectedKycImages.aadhaar.front.imageUrl, IMAGE_NOT_FOUND_PLACEHOLDER)
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Aadhaar Back */}
                    {selectedKycImages.aadhaar?.back?.imageUrl && (
                      <div className="flex flex-col border rounded-lg p-2 shadow-sm">
                        <span className="font-bold text-gray-700 mb-2 border-b pb-1">Aadhaar Back</span>
                        <div className="flex-1 flex items-center justify-center bg-gray-100 rounded">
                          <img
                            src={resolveUploadUrl(selectedKycImages.aadhaar.back.imageUrl)}
                            alt="Aadhaar Back"
                            className="w-full object-contain hover:scale-105 transition-transform duration-200 cursor-zoom-in"
                            onClick={() => {
                              const imageUrl = resolveUploadUrl(selectedKycImages.aadhaar.back.imageUrl)
                              if (imageUrl) window.open(imageUrl, '_blank')
                            }}
                             onError={(e) => {
                                handleUploadImageError(e, selectedKycImages.aadhaar.back.imageUrl, IMAGE_NOT_FOUND_PLACEHOLDER)
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {!selectedKycImages.pan?.imageUrl &&
                    !selectedKycImages.aadhaar?.front?.imageUrl &&
                    !selectedKycImages.aadhaar?.back?.imageUrl && (
                      <div className="text-center py-10 text-gray-500 italic">
                        No images found for this player.
                      </div>
                    )}
                </div>

                {/* Footer */}
                <div className='flex items-center justify-end py-3 px-4 border-t border-solid border-gray-200 rounded-b bg-gray-50'>
                  <button
                    className='bg-gray-500 text-white active:bg-gray-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none ease-linear transition-all duration-150'
                    type='button'
                    onClick={closeKycModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className='opacity-40 fixed inset-0 z-40 bg-black'></div>
        </>
      )}
    </div>
  )
}

export default Players
